import { C, fanMail, staffNames, stores, type FanLetter } from "@bd/content";
import { WEEKS_PER_YEAR } from "./calendar";
import { BALANCE } from "./balance";
import { addDecision, DECISION_HANDLERS } from "./company";
import { eventScales, roundNice } from "./director";
import { dlog10 } from "./dmath";
import { lineOptions } from "./lines";
import type { Rng } from "./rng";
import { fanMarket } from "./sales";
import type { Company, GameState, Product } from "./state";
import { clamp, digits, log, rng } from "./util";

/**
 * Fan mail: every few months (more often with more fans) a letter about one of your games, by how well it scored
 * and who it's for. Some letters ask for something (a sequel, an expansion, a reprint): promise it and deliver
 * within a year for fans and reputation; promise and forget, and the fan remembers.
 */

export type FanAsk = "sequel" | "expansion" | "reprint";

export interface FanPromise {
  kind: FanAsk;
  product: string;
  game: string;
  fan: string;
  town: string;
  since: number;
  due: number;
}

export interface FanMailState {
  /** the week the next letter arrives */
  next: number;
  letters: number;
  promises: FanPromise[];
}

const SEQUEL_KINDS = new Set(["spinoff", "edition"]);
const MORE_KINDS = new Set(["expansion", "card_set", "rpg_supplement"]);

const fill = (text: string, v: Record<string, string>) => text.replace(/\{(\w+)\}/g, (m, k: string) => v[k] ?? m);

/** Weeks until the next letter: 14–30, down to about half that for a publisher with a big following. */
function gap(state: GameState, co: Company, r: Rng): number {
  const [lo, hi] = BALANCE.fanmailGap;
  const pull = 1 + 0.8 * dlog10(1 + co.fans / fanMarket(state.week));
  return Math.max(4, Math.round(r.int(lo, hi) / pull));
}

export function scoreBand(score: number): FanLetter["band"] {
  return score >= 8 ? "high" : score >= 5.5 ? "mid" : "low";
}

/** Games fans write about: your own boxes from the last six years, and evergreens. */
function writable(state: GameState, co: Company): Product[] {
  return co.products.filter((p) => p.launched && !p.retired && ["base", "spinoff", "license", "edition"].includes(p.kind) && (p.evergreen || state.week - p.releaseWeek < 6 * WEEKS_PER_YEAR));
}

/** What a fan could reasonably ask of this game now. */
export function possibleAsks(state: GameState, co: Company, p: Product): FanAsk[] {
  const band = scoreBand(p.score);
  if (band === "low") return [];
  const out: FanAsk[] = [];
  if (p.stock === 0 && !p.orders.length && p.soldOutWeeks > 0) out.push("reprint");
  const opts = lineOptions(state, co, p);
  if (opts.some((o) => o.ok && MORE_KINDS.has(o.kind))) out.push("expansion");
  if (band === "high" && opts.some((o) => o.ok && SEQUEL_KINDS.has(o.kind))) out.push("sequel");
  return out;
}

/** A letter arrives: a note in the log, or (now and then) a request you answer. */
export function sendLetter(state: GameState, co: Company, r: Rng, forceAsk = false): void {
  const fm = co.fanmail!;
  const games = writable(state, co);
  if (!games.length) return;
  const p = r.weighted(games, (g) => Math.sqrt(1 + g.sold) * (g.score >= 8 ? 1.5 : 1));
  const fan = r.pick(staffNames.first);
  const town = r.pick(stores).name;
  const v = { game: p.design.name, theme: C.theme[p.design.theme]?.name ?? p.design.theme, fan, town };
  fm.letters += 1;
  const asks = possibleAsks(state, co, p);
  const open = co.pending.some((d) => d.kind === "fanmail") || fm.promises.length >= 2;
  if (asks.length && !open && (forceAsk || r.chance(BALANCE.fanmailAskP))) {
    const kind = r.pick(asks);
    const ask = fanMail.asks[kind];
    const scales = eventScales(state, co);
    const promiseFans = roundNice(BALANCE.fanmailPromiseFans * scales.fanScale);
    const thanksFans = roundNice(BALANCE.fanmailThanksFans * scales.fanScale);
    addDecision(state, co, {
      kind: "fanmail",
      title: fill(ask.title, v),
      text: fill(ask.text, v),
      options: [
        { id: "promise", label: ask.promise, detail: fill(ask.promiseDetail, { fans: digits(promiseFans) }) },
        { id: "thanks", label: fanMail.thanks, detail: fill(fanMail.thanksDetail, { fans: digits(thanksFans) }) },
      ],
      defaultOption: "thanks",
      weeks: 6,
      data: { ask: kind, product: p.id, game: p.design.name, fan, town, promiseFans, thanksFans },
    });
    return;
  }
  const band = scoreBand(p.score);
  const pool = fanMail.letters.filter((l) => l.band === band && (!l.audience || l.audience.includes(p.design.audience)));
  const letter = r.pick(pool);
  log(state, "fanmail", fill(fanMail.lines.letter, { fan, town, letter: fill(letter.text, v) }), co.id);
}

DECISION_HANDLERS.fanmail = (state, co, d, option) => {
  const x = d.data ?? {};
  const fm = (co.fanmail ??= { next: state.week + 26, letters: 0, promises: [] });
  const v = { fan: String(x.fan), town: String(x.town), game: String(x.game) };
  if (option === "promise") {
    fm.promises.push({ kind: x.ask, product: x.product, game: x.game, fan: x.fan, town: x.town, since: state.week, due: state.week + WEEKS_PER_YEAR });
    log(state, "fanmail_reply", fill(fanMail.lines.promised, v), co.id);
  } else {
    co.fans += Number(x.thanksFans) || 0;
    log(state, "fanmail_reply", fill(fanMail.lines.thanked, v), co.id);
  }
};

/** Has the promise been kept: the follow-up is out, or the reprint is ordered or in? */
export function promiseKept(co: Company, pr: FanPromise): boolean {
  if (pr.kind === "reprint") {
    const p = co.products.find((x) => x.id === pr.product);
    return !!p && (p.orders.length > 0 || p.stock > 0);
  }
  const kinds = pr.kind === "sequel" ? SEQUEL_KINDS : MORE_KINDS;
  return co.products.some((x) => x.parentId === pr.product && kinds.has(x.kind) && x.launched && x.releaseWeek >= pr.since);
}

function promisesWeek(state: GameState, co: Company): void {
  const fm = co.fanmail!;
  if (!fm.promises.length) return;
  const keep: FanPromise[] = [];
  for (const pr of fm.promises) {
    const v = { fan: pr.fan, town: pr.town, game: pr.game };
    if (promiseKept(co, pr)) {
      const fans = roundNice(BALANCE.fanmailPromiseFans * eventScales(state, co).fanScale);
      co.fans += fans;
      co.reputation = clamp(co.reputation + 2, 0, 100);
      log(state, "fanmail", fill(fanMail.lines.kept, { ...v, fans: digits(fans) }), co.id);
    } else if (state.week >= pr.due) {
      co.reputation = clamp(co.reputation - 1, 0, 100);
      log(state, "fanmail_late", fill(fanMail.lines.broken, v), co.id);
    } else keep.push(pr);
  }
  fm.promises = keep;
}

export const fanMailSystem = {
  name: "fanmail",
  week(state: GameState): void {
    for (const co of state.companies) {
      if (co.bankrupt || !co.products.some((p) => p.launched)) continue;
      const r = rng(state, `fanmail:${co.id}`);
      // the first letter comes a few months after the first game is out
      if (!co.fanmail) {
        co.fanmail = { next: state.week + gap(state, co, r), letters: 0, promises: [] };
        continue;
      }
      promisesWeek(state, co);
      if (state.week < co.fanmail.next) continue;
      sendLetter(state, co, r);
      co.fanmail.next = state.week + gap(state, co, r);
    }
  },
};
